import { VaBreadcrumbs } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { toggleValues } from '@department-of-veterans-affairs/platform-site-wide/selectors';
import FEATURE_FLAG_NAMES from '@department-of-veterans-affairs/platform-utilities/featureFlagNames';
import fastLevenshtein from 'fast-levenshtein';
import { isValidYear } from 'platform/forms-system/src/js/utilities/validations';
import {
  checkboxGroupSchema,
  checkboxGroupUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import { apiRequest } from 'platform/utilities/api';
import _ from 'platform/utilities/data';
import PropTypes from 'prop-types';
import React from 'react';
import { createSelector } from 'reselect';

import {
  add,
  getYear,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  isWithinInterval,
} from 'date-fns';
import {
  CHAR_LIMITS,
  DATA_PATHS,
  DISABILITY_526_V2_ROOT_URL,
  FORM_STATUS_BDD,
  HOMELESSNESS_TYPES,
  NEW_CONDITION_OPTION,
  NINE_ELEVEN,
  NULL_CONDITION_STRING,
  PAGE_TITLES,
  PTSD_MATCHES,
  RESERVE_GUARD_TYPES,
  START_TEXT,
  TYPO_THRESHOLD,
  USA,
  itfStatuses,
} from '../constants';
import { applyConditionsWorkflowTransformation } from './conditionsWorkflowTransformations';
import { incrementConditionsTransformCount } from './tracking/features/conditionsWorkflowMigrationTracking';
import {
  getRestartDestination,
  prepareRestartRedirect,
} from './restartRouting';
import { getBranches } from './serviceBranches';
import { setSharedVariable } from './sharedState';
import { verifyPrefilledZipCode } from '../../shared/utils/zipCodeData';
import { isBDD, getBddShaUploads, hasShaDocumentUploaded } from './bdd';
import {
  formatDateRange,
  formatDate,
  parseDate,
  getToday,
  isExpired,
} from '../../shared/utils/dates';

/**
 * Returns an object where all the fields are prefixed with `view:` if they aren't already
 */
export const viewifyFields = formData => {
  const newFormData = {};
  Object.keys(formData).forEach(key => {
    const viewKey = /^view:/.test(key) ? key : `view:${key}`;
    // Recurse if necessary
    newFormData[viewKey] =
      typeof formData[key] === 'object' && !Array.isArray(formData[key])
        ? viewifyFields(formData[key])
        : formData[key];
  });
  return newFormData;
};

/**
 * Show one thing, have a screen reader say another.
 *
 * @param {ReactElement|ReactComponent|String} srIgnored -- Thing to be displayed visually,
 *                                                           but ignored by screen readers
 * @param {String} substitutionText -- Text for screen readers to say instead of srIgnored
 */
export const srSubstitute = (srIgnored, substitutionText) => (
  <span>
    <span aria-hidden>{srIgnored}</span>
    <span className="sr-only">{substitutionText}</span>
  </span>
);

export const isUndefined = value => (value || '') === '';

// Check if expiration date is today or in the future
export const isNotExpired = (expirationDate = '') => {
  const today = new Date(getToday());
  const expiration = parseDate(expirationDate);
  if (!today || !expiration) return false;
  // Check if today is before or same as expiration (i.e., expiration is today or future)
  return isBefore(today, expiration) || isSameDay(today, expiration);
};

export const isValidFullDate = dateString => {
  // expecting dateString = 'yyyy-MM-dd'
  const date = parseDate(dateString);
  return (
    (date &&
      isValid(date) &&
      // date-fns parse('2021') would be invalid
      // date-fns parse('XXXX-01-01') would be invalid
      dateString === formatDate(date, 'yyyy-MM-dd') &&
      // make sure we're within the min & max year range
      isValidYear(getYear(date))) ||
    false
  );
};

export const isValidServicePeriod = data => {
  const { serviceBranch, dateRange: { from = '', to = '' } = {} } = data || {};
  return (
    (!isUndefined(serviceBranch) &&
      getBranches().includes(serviceBranch) &&
      !isUndefined(from) &&
      !isUndefined(to) &&
      isValidFullDate(from) &&
      isValidFullDate(to) &&
      isBefore(parseDate(from), parseDate(to))) ||
    false
  );
};

export const isActiveITF = currentITF => {
  if (currentITF) {
    const isActive = currentITF.status === itfStatuses.active;
    return isActive && isNotExpired(currentITF.expirationDate);
  }
  return false;
};

export const hasGuardOrReservePeriod = formData => {
  const serviceHistory = formData?.servicePeriods;
  if (!serviceHistory || !Array.isArray(serviceHistory)) {
    return false;
  }

  return serviceHistory.reduce((isGuardReserve, { serviceBranch }) => {
    // For a new service period, service branch defaults to undefined
    if (!serviceBranch) {
      return isGuardReserve;
    }
    const { nationalGuard, reserve } = RESERVE_GUARD_TYPES;
    return (
      isGuardReserve ||
      serviceBranch.includes(reserve) ||
      serviceBranch.includes(nationalGuard)
    );
  }, false);
};

export const ReservesGuardDescription = ({ formData }) => {
  const { servicePeriods } = formData || {};
  if (
    !servicePeriods ||
    !Array.isArray(servicePeriods) ||
    !servicePeriods[0].serviceBranch
  ) {
    return null;
  }

  const mostRecentPeriod = servicePeriods
    .filter(({ serviceBranch }) => {
      const { nationalGuard, reserve } = RESERVE_GUARD_TYPES;
      return (
        serviceBranch.includes(nationalGuard) || serviceBranch.includes(reserve)
      );
    })
    .map(({ serviceBranch, dateRange }) => {
      const dateTo = new Date(dateRange.to);
      return {
        serviceBranch,
        to: dateTo,
      };
    })
    .sort((periodA, periodB) => periodB.to - periodA.to)[0];

  if (!mostRecentPeriod) {
    return null;
  }
  const { serviceBranch, to } = mostRecentPeriod;
  return (
    <div>
      Please tell us more about your {serviceBranch} service that ended on{' '}
      {formatDate(to)}.
    </div>
  );
};

ReservesGuardDescription.propTypes = {
  formData: PropTypes.object,
};

export const title10DatesRequired = formData =>
  _.get(
    'serviceInformation.reservesNationalGuardService.view:isTitle10Activated',
    formData,
    false,
  );

const capitalizeWord = word => {
  const capFirstLetter = word[0].toUpperCase();
  return `${capFirstLetter}${word.slice(1)}`;
};

/**
 * Takes a string and returns the same string with every word capitalized. If no valid
 * string is given as input, returns NULL_CONDITION_STRING.
 * @param {string} name the lower-case name of a disability
 * @returns {string} the input name, but with all words capitalized
 */
export const capitalizeEachWord = name => {
  if (name && typeof name === 'string') {
    return name.replace(/\w[^\s-]*/g, capitalizeWord);
  }

  return null;
};

export const hasForwardingAddress = formData =>
  _.get('view:hasForwardingAddress', formData, false);

export const forwardingCountryIsUSA = formData =>
  _.get('forwardingAddress.country', formData, '') === USA;

export function getSeparationLocations() {
  return apiRequest('/disability_compensation_form/separation_locations')
    .then(({ separationLocations }) =>
      separationLocations.map(separationLocation => ({
        id: separationLocation.code,
        label: separationLocation.description,
      })),
    )
    .catch(() => {
      // Error getting separation locations
      return [];
    });
}

export const disabilityIsSelected = disability => disability['view:selected'];

/**
 * Takes a string and returns another that won't break SiP when used
 * as a property name.
 * @param {string} str - The string to make SiP-friendly
 * @return {string} The SiP-friendly string
 */
const regexNonWord = /[^\w]/g;
export const sippableId = str =>
  (str || 'blank').replace(regexNonWord, '').toLowerCase();

/**
 * Returns true when the evidence enhancement flow is active for this form.
 * Gated purely by the feature toggle — applies to all users (net-new and IPFs).
 *
 * @param {Object} formData
 * @returns {boolean}
 */
export const isEvidenceEnhancement = formData =>
  !!formData?.disability526SupportingEvidenceEnhancement;

export const hasVAEvidence = formData =>
  _.get(DATA_PATHS.hasVAEvidence, formData, false);
export const hasOtherEvidence = formData =>
  _.get(DATA_PATHS.hasAdditionalDocuments, formData, false);
export const hasPrivateEvidence = formData =>
  _.get(DATA_PATHS.hasPrivateEvidence, formData, false);

export const getVaEvidence = formData =>
  _.get('vaTreatmentFacilities', formData, []);
export const getPrivateFacilities = formData =>
  _.get('providerFacility', formData, []);
export const getPrivateEvidenceUploads = formData =>
  _.get('privateMedicalRecordAttachments', formData, []);
export const getAdditionalDocuments = formData =>
  _.get('additionalDocuments', formData, []);

/**
 * Single source of truth for deriving view:hasMedicalRecords from legacy
 * evidence fields. Returns undefined when there isn't enough information to
 * safely infer an answer — callers must treat undefined as "leave it alone,"
 * never coerce it to false.
 *
 * Medical records derive ONLY from the VA/Private selections
 * (`view:hasVaMedicalRecords` / `view:hasPrivateMedicalRecords`). Crucially it
 * does NOT consider `view:hasOtherEvidence`: in the enhancement flow "other
 * supporting documents" is a *separate* question (evidenceChoiceIntro) that
 * writes to the same shared `view:selectableEvidenceTypes` object. If
 * hasOtherEvidence counted here, answering that separate question would make
 * the medical-records answer look "derivable" (as false) and silently satisfy
 * its required check — letting a Veteran submit without ever answering whether
 * they have medical records.
 *
 * - `view:hasEvidence` unanswered  -> undefined (nothing to derive from)
 * - `view:hasEvidence` === false   -> false (no evidence at all, unambiguous)
 * - VA or Private explicitly true  -> true
 * - otherwise (VA/Private not selected) -> undefined (can't infer; the Veteran
 *   must answer the medical-records question)
 *
 * @param {Object} formData
 * @returns {boolean|undefined}
 */
export const deriveMedicalRecordsFromLegacyData = formData => {
  const hasEvidence = _.get(DATA_PATHS.hasEvidence, formData);
  if (hasEvidence === undefined) {
    return undefined;
  }
  if (hasEvidence === false) {
    return false;
  }
  return hasVAEvidence(formData) || hasPrivateEvidence(formData)
    ? true
    : undefined;
};

export const hasMedicalRecords = formData => {
  if (isEvidenceEnhancement(formData)) {
    // Enhancement flow: check new field name, with fallback to legacy data for transition compatibility
    const hasRecords = _.get(DATA_PATHS.hasMedicalRecords, formData);
    if (hasRecords !== undefined) {
      return hasRecords;
    }
    // Transition compatibility: derive from legacy data if switching from
    // legacy flow. This is a routing/branching helper, so an ambiguous
    // (undefined) derivation intentionally coerces to false here.
    return !!deriveMedicalRecordsFromLegacyData(formData);
  }
  // Legacy flow: use the existing path
  return _.get(DATA_PATHS.hasEvidence, formData, false);
};

/**
 * Reconciles evidence view fields for resumed IPFs entering the enhanced flow.
 *
 * Derives `view:hasMedicalRecords` from legacy evidence fields (see
 * deriveMedicalRecordsFromLegacyData) ONLY when there's enough legacy data to
 * safely infer an answer. When there is nothing to derive from, the field is
 * intentionally left absent so required-field validation blocks submission
 * and prompts the Veteran to return to the supporting-evidence section (via
 * the review error link). This never fabricates an answer.
 *
 * @param {Object} formData
 * @returns {Object} formData (a new object only when a value was derived)
 */
export const reconcileEvidenceViewFields = formData => {
  if (!isEvidenceEnhancement(formData)) {
    return formData;
  }
  // Already answered under the enhanced flow — nothing to reconcile.
  if (_.get(DATA_PATHS.hasMedicalRecords, formData) !== undefined) {
    return formData;
  }
  const derived = deriveMedicalRecordsFromLegacyData(formData);
  if (derived === undefined) {
    return formData;
  }

  return {
    ...formData,
    [DATA_PATHS.hasMedicalRecords]: derived,
  };
};

/**
 * Whether the medical-records question has an answer to derive from — either
 * the enhanced field itself, or legacy fields it can be safely migrated from
 * (see deriveMedicalRecordsFromLegacyData). Used to make the evidenceRequest
 * page's `required` schema dynamic, so full-form validation doesn't block
 * submission for legacy IPFs that answered the question under the old flow
 * but never revisited this page after the enhancement toggle turned on for
 * their session — while still requiring an answer when the legacy data is
 * too ambiguous to derive from (e.g. "yes" with no evidence-type selection
 * reached).
 *
 * @param {Object} formData
 * @returns {boolean}
 */
export const isMedicalRecordsAnswered = formData =>
  _.get(DATA_PATHS.hasMedicalRecords, formData) !== undefined ||
  deriveMedicalRecordsFromLegacyData(formData) !== undefined;

/**
 * Inspects all given paths in the formData object for presence of values
 * @param {object} formData  full formData for the form
 * @param {array} fieldPaths full paths in formData for other fields that
 *                           should be checked for input
 * @returns {boolean} true if at least one path is not empty / false otherwise
 */
export const fieldsHaveInput = (formData, fieldPaths) =>
  fieldPaths.some(path => !!_.get(path, formData, ''));

export const bankFieldsHaveInput = formData =>
  fieldsHaveInput(formData, [
    'view:bankAccount.bankAccountType',
    'view:bankAccount.bankAccountNumber',
    'view:bankAccount.bankRoutingNumber',
    'view:bankAccount.bankName',
  ]);

/**
 * Returns the path with any ':index' substituted with the actual index.
 * @param {string} path - The path with or without ':index'
 * @param {number} index - The index to put in the string
 * @return {string}
 */
export const pathWithIndex = (path, index) => path.replace(':index', index);

const post911Periods = createSelector(
  data => _.get('serviceInformation.servicePeriods', data, []),
  periods =>
    periods.filter(({ dateRange }) => {
      if (!(dateRange && dateRange.to)) {
        return false;
      }

      const toDate = new Date(dateRange.to);
      const cutOff = new Date(NINE_ELEVEN);
      return toDate.getTime() > cutOff.getTime();
    }),
);

export const servedAfter911 = formData => !!post911Periods(formData).length;

export const isDisabilityPtsd = disability => {
  if (!disability || typeof disability !== 'string') {
    return false;
  }

  const strippedDisability = disability.toLowerCase().replace(/[^a-zA-Z]/g, '');

  return PTSD_MATCHES.some(ptsdString => {
    const strippedString = ptsdString.replace(/[^a-zA-Z]/g, '');
    if (strippedString === strippedDisability) {
      return true;
    }

    // does the veteran's input contain a string from our match list?
    if (strippedDisability.includes(strippedString)) {
      return true;
    }

    return (
      fastLevenshtein.get(strippedString, strippedDisability) <
      Math.ceil(strippedDisability.length * TYPO_THRESHOLD)
    );
  });
};

export const hasRatedDisabilities = formData =>
  formData?.ratedDisabilities?.length > 0;

export const isClaimingNew = formData =>
  _.get(
    'view:claimType.view:claimingNew',
    formData,
    // force default to true if user has no rated disabilities
    !hasRatedDisabilities(formData),
  );

export const isClaimingIncrease = formData =>
  _.get('view:claimType.view:claimingIncrease', formData, false);

export { isBDD };

export const hasNewPtsdDisability = formData =>
  !isBDD(formData) &&
  isClaimingNew(formData) &&
  _.get('newDisabilities', formData, []).some(disability =>
    isDisabilityPtsd(disability.condition),
  ) &&
  // hasNewPtsdDisability gates the existing Form 0781 flow
  // When the syncModern0781Flow flipper is set to true, we will display a new version of the flow.
  // When the new version is visible, hasNewPtsdDisability should always return false so the legacy flow is hidden.
  formData?.syncModern0781Flow !== true;

export const showPtsdCombat = formData =>
  hasNewPtsdDisability(formData) &&
  _.get('view:selectablePtsdTypes.view:combatPtsdType', formData, false);

export const showPtsdNonCombat = formData =>
  hasNewPtsdDisability(formData) &&
  _.get('view:selectablePtsdTypes.view:nonCombatPtsdType', formData, false) &&
  // skip non-combat question if Veteran says yes to combat question
  !_.get('skip781ForCombatReason', formData, false);

export const skip781 = formData =>
  _.get('skip781ForCombatReason', formData) === true ||
  _.get('skip781ForNonCombatReason', formData) === true;

export const needsToEnter781 = formData =>
  hasNewPtsdDisability(formData) &&
  (showPtsdCombat(formData) || showPtsdNonCombat(formData)) &&
  !skip781(formData);

export const needsToEnter781a = formData =>
  hasNewPtsdDisability(formData) &&
  (_.get('view:selectablePtsdTypes.view:mstPtsdType', formData, false) ||
    _.get('view:selectablePtsdTypes.view:assaultPtsdType', formData, false));

export const isUploading781Form = formData =>
  _.get('view:upload781Choice', formData, '') === 'upload';

export const isUploading781aForm = formData =>
  _.get('view:upload781aChoice', formData, '') === 'upload';

export const isAnswering781Questions = index => formData =>
  needsToEnter781(formData) &&
  _.get('view:upload781Choice', formData, '') === 'answerQuestions' &&
  (_.get(`view:enterAdditionalEvents${index - 1}`, formData, false) ||
    index === 0);

export const isAnswering781aQuestions = index => formData =>
  needsToEnter781a(formData) &&
  _.get('view:upload781aChoice', formData, '') === 'answerQuestions' &&
  (_.get(`view:enterAdditionalSecondaryEvents${index - 1}`, formData, false) ||
    index === 0);

export const isUploading781aSupportingDocuments = index => formData =>
  isAnswering781aQuestions(index)(formData) &&
  _.get(`secondaryIncident${index}.view:uploadSources`, formData, false);

export const isAddingIndividuals = index => formData =>
  isAnswering781Questions(index)(formData) &&
  _.get(`view:individualsInvolved${index}`, formData, false);

export const isUploading8940Form = formData =>
  _.get('view:unemployabilityUploadChoice', formData, '') === 'upload';

export const getHomelessOrAtRisk = formData => {
  const homelessStatus = _.get('homelessOrAtRisk', formData, '');
  return (
    homelessStatus === HOMELESSNESS_TYPES.homeless ||
    homelessStatus === HOMELESSNESS_TYPES.atRisk
  );
};

export const isNotUploadingPrivateMedical = formData =>
  _.get(DATA_PATHS.hasPrivateRecordsToUpload, formData) === false;

// Explicit "Yes, I'll upload my private medical records". This is NOT the same
// as `!isNotUploadingPrivateMedical`, which is also true when the choice is
// unanswered (undefined !== false). The upload pages must only appear after an
// explicit "yes", so they gate on this rather than the negation.
export const isUploadingPrivateMedical = formData =>
  _.get(DATA_PATHS.hasPrivateRecordsToUpload, formData) === true;

export const hasCompletedAuthorization = value => value === true;

export const needsToEnterUnemployability = formData =>
  _.get('view:unemployable', formData, false);

export const needsToAnswerUnemployability = formData =>
  needsToEnterUnemployability(formData) &&
  _.get('view:unemployabilityUploadChoice', formData, '') === 'answerQuestions';

export const hasDoctorsCare = formData =>
  needsToAnswerUnemployability(formData) &&
  _.get('unemployability.underDoctorsCare', formData, false);

export const hasHospitalCare = formData =>
  needsToAnswerUnemployability(formData) &&
  _.get('unemployability.hospitalized', formData, false);

export const isUploadingSupporting8940Documents = formData =>
  needsToAnswerUnemployability(formData) &&
  _.get('view:uploadUnemployabilitySupportingDocumentsChoice', formData, false);

export const wantsHelpWithOtherSourcesSecondary = index => formData =>
  _.get(`secondaryIncident${index}.otherSources`, formData, '') &&
  isAnswering781aQuestions(index)(formData);

export const wantsHelpWithPrivateRecordsSecondary = index => formData =>
  _.get(
    `secondaryIncident${index}.otherSourcesHelp.view:helpPrivateMedicalTreatment`,
    formData,
    '',
  ) &&
  isAnswering781aQuestions(index)(formData) &&
  wantsHelpWithOtherSourcesSecondary(index)(formData);

export const wantsHelpRequestingStatementsSecondary = index => formData =>
  _.get(
    `secondaryIncident${index}.otherSourcesHelp.view:helpRequestingStatements`,
    formData,
    '',
  ) &&
  isAnswering781aQuestions(index)(formData) &&
  wantsHelpWithOtherSourcesSecondary(index)(formData);

const isDateRange = ({ from, to }) => !!(from && to);

// NOTE: Could move this to outside all-claims
/**
 * Checks to see if the first parameter is inside the date range (second parameter).
 * If the first parameter is a date range, it'll return true if both dates are inside the range.
 * @typedef {Object} DateRange
 * @property {string} to - A date string YYYY-MM-DD
 * @property {string} from - A date string YYYY-MM-DD
 * ---
 * @param {String|DateRange} inside - The date or date range to check
 * @param {DateRange} outside - The range `inside` must fit in
 * @param {String} inclusivity - See https://momentjs.com/docs/#/query/is-between/
 *                               NOTE: This function defaults to inclusive dates which is different
 *                               from moment's default
 */
export const isWithinRange = (inside, outside, inclusivity = '[]') => {
  if (isDateRange(inside)) {
    return (
      isWithinRange(inside.to, outside, inclusivity) &&
      isWithinRange(inside.from, outside, inclusivity)
    );
  }
  if (typeof inside !== 'string') return false;

  const insideDate = parseDate(inside);
  const from = parseDate(outside.from);
  const to = parseDate(outside.to);

  if (!insideDate || !from || !to) return false;
  if (!isValid(insideDate) || !isValid(from) || !isValid(to)) return false;

  // isWithinInterval is inclusive by default, matching inclusivity='[]'
  return isWithinInterval(insideDate, { start: from, end: to });
};

// This is in here instead of validations.js because it returns a jsx element
export const getPOWValidationMessage = servicePeriodDateRanges => (
  <span>
    The dates you enter must be within one of the service periods you entered.
    <ul>
      {servicePeriodDateRanges.map((range, index) => (
        <li key={index}>{formatDateRange(range)}</li>
      ))}
    </ul>
  </span>
);

export const increaseOnly = formData =>
  isClaimingIncrease(formData) && !isClaimingNew(formData);
export const newConditionsOnly = formData =>
  !isClaimingIncrease(formData) && isClaimingNew(formData);
export const newAndIncrease = formData =>
  isClaimingNew(formData) && isClaimingIncrease(formData);

// Shouldn't be possible, but just in case this requirement is lifted later...
export const noClaimTypeSelected = formData =>
  !isClaimingNew(formData) && !isClaimingIncrease(formData);

/**
 * The base urls for each form
 * @readonly
 * @enum {String}
 */
export const urls = {
  v2: DISABILITY_526_V2_ROOT_URL,
};

/**
 * Returns the base url of whichever form the user needs to go to.
 *
 * @param {Object} formData - The saved form data
 * @param {Boolean} isPrefill - True if formData comes from pre-fill, false if it's a saved form
 * @return {String} - The base url of the right form to return to
 */

export const claimingRated = formData =>
  formData?.ratedDisabilities?.some(d => d['view:selected']);

export const isPlaceholderRated = v => v === 'Rated Disability';

export const isNewConditionOption = v => v === NEW_CONDITION_OPTION;

// TE/POW should only show when there’s at least one *real* condition
export const hasRealNewOrSecondaryConditions = formData =>
  Array.isArray(formData?.newDisabilities) &&
  formData.newDisabilities.some(
    d =>
      typeof d?.condition === 'string' &&
      !isPlaceholderRated(d.condition) &&
      (d?.cause === 'NEW' || d?.cause === 'SECONDARY'),
  );
// TODO: Rename this to avoid collision with `isClaimingNew` above
export const claimingNew = formData =>
  formData?.newDisabilities?.some(d => d.condition);

export const hasClaimedConditions = formData =>
  (isClaimingIncrease(formData) && claimingRated(formData)) ||
  (isClaimingNew(formData) && claimingNew(formData));

/**
 * Finds active service periods—those without end dates or end dates
 * in the future.
 */
export const activeServicePeriods = formData =>
  _.get('serviceInformation.servicePeriods', formData, []).filter(
    sp => !sp.dateRange.to || isAfter(parseDate(sp.dateRange.to), getToday()),
  );

/**
 * Determines if the user is uploading service treatment records (STR) for a BDD claim.
 * @param {*} formData
 * @returns {boolean} - True if the user is uploading STR for a BDD claim, false otherwise
 */
export const isUploadingSTR = formData =>
  isBDD(formData) &&
  _.get(
    'view:uploadServiceTreatmentRecordsQualifier.view:hasServiceTreatmentRecordsToUpload',
    formData,
    false,
  );

export const isBddShaWorkflowActive = formData =>
  formData.disability526NewBddShaEnforcementWorkflowEnabled && isBDD(formData);

export const isUploadingBddSha = formData =>
  _.get('view:hasSeparationHealthAssessment', formData, false) &&
  isBddShaWorkflowActive(formData);

export { getBddShaUploads, hasShaDocumentUploaded };

export const DISABILITY_SHARED_CONFIG = {
  orientation: {
    path: 'disabilities/orientation',
    // Only show the page if both (or potentially neither) options are chosen on the claim-type page
    depends: formData => newAndIncrease(formData) && !isBDD(formData),
  },
  ratedDisabilities: {
    path: 'disabilities/rated-disabilities',
    depends: formData => isClaimingIncrease(formData),
  },
  addDisabilities: {
    path: 'new-disabilities/add',
    depends: isClaimingNew,
  },
};

export const getPageTitle = formData => {
  const showBDDTitle =
    formData === true ||
    isBDD(formData) ||
    window.sessionStorage.getItem(FORM_STATUS_BDD) === 'true';
  return PAGE_TITLES[showBDDTitle ? 'BDD' : 'ALL'];
};

// Intro page doesn't have formData
export const getStartText = isBDDForm => {
  const showBDDText =
    isBDDForm ||
    isBDD() ||
    window.sessionStorage.getItem(FORM_STATUS_BDD) === 'true';
  return START_TEXT[showBDDText ? 'BDD' : 'ALL'];
};

export const showSeparationLocation = formData => {
  const { serviceInformation = {} } = formData || {};
  const { servicePeriods, reservesNationalGuardService } = serviceInformation;

  // moment(undefined) => today
  // moment(null) => Invalid date
  const title10SeparationDate = parseDate(
    reservesNationalGuardService?.title10Activation
      ?.anticipatedSeparationDate || null,
  );

  if (
    (!title10SeparationDate || !isValid(title10SeparationDate)) &&
    (!servicePeriods || !Array.isArray(servicePeriods))
  ) {
    return false;
  }

  const today = getToday();
  const todayPlus180 = add(today, { days: 180 });

  // Show separation location field if activated on federal orders & < 180 days
  if (
    title10SeparationDate &&
    isValid(title10SeparationDate) &&
    isAfter(title10SeparationDate, today) &&
    !isAfter(title10SeparationDate, todayPlus180)
  ) {
    return true;
  }

  const mostRecentDate = servicePeriods
    ?.filter(({ dateRange }) => dateRange?.to)
    .map(({ dateRange }) => parseDate(dateRange.to))
    .filter(date => date && isValid(date))
    .sort((dateA, dateB) => dateB - dateA)[0];

  return mostRecentDate
    ? isAfter(mostRecentDate, today) && !isAfter(mostRecentDate, todayPlus180)
    : false;
};

export const show526Wizard = state => toggleValues(state).show526Wizard;

export const showSubform8940And4192 = state =>
  toggleValues(state)[FEATURE_FLAG_NAMES.subform89404192];

/**
 * Selector to check if the toxic exposure destruction modal feature flag is enabled.
 * @param {Object} state - Redux state object
 * @returns {boolean} True if the feature flag is enabled, false otherwise
 */
export const showToxicExposureDestructionModal = state =>
  toggleValues(state).disabilityCompensationToxicExposureDestructionModal;

/**
 * Selector to check if the toxic exposure opt-out data purge feature flag is enabled.
 * @param {Object} state - Redux state object
 * @returns {boolean} True if the feature flag is enabled, false otherwise
 */
export const showToxicExposureOptOutDataPurge = state =>
  toggleValues(state).disability526ToxicExposureOptOutDataPurge;

export const wrapWithBreadcrumb = (
  title,
  component,
  href = '/disability/file-disability-claim-form-21-526ez',
) => (
  <>
    <div className="row vads-u-padding-x--1p5">
      <VaBreadcrumbs
        uswds
        breadcrumbList={[
          { href: '/', label: 'Home' },
          { href: '/disability', label: 'Disability Benefits' },
          {
            href,
            label: title,
          },
        ]}
      />
    </div>
    {component}
  </>
);

// Re-exported from the shared dates module to keep this import path stable.
export { isExpired };

/**
 * @typedef NewDisability~entry
 * @property {String} condition - disability name
 * @property {String} cause - disability type
 * @property {String} primaryDescription - new disability description
 * @property {String} causedByDisabilityDescription - name of rated disability
 * @property {String} worsenedDescription - worsened description
 * @property {String} worsenedEffects - result
 * @property {String} vaMistreatmentDescription - VA involved
 * @property {String} vaMistreatmentLocation - location
 * @property {String} vaMistreatmentDate - date
 */
/**
 * Truncate long descriptions
 * @param {NewDisability~entry} data - new disability array entry
 * @returns new disability array entry with over-the-limit descriptions
 *  truncated
 */
export const truncateDescriptions = data =>
  Object.keys(data).reduce(
    (entry, key) => ({
      ...entry,
      [key]:
        key in CHAR_LIMITS
          ? data[key].substring(0, CHAR_LIMITS[key])
          : data[key],
    }),
    {},
  );

/**
 * Creates consistent form title
 * @param {string} title
 * @returns {string} markup with h3 tag and consistent styling
 */
export const formTitle = title => (
  <h3 className="vads-u-font-size--h4 vads-u-color--base vads-u-margin--0">
    {title}
  </h3>
);

/**
 * Creates consistent form subtitle
 * @param {string} subtitle
 * @returns {string} markup with h4 tag and consistent styling
 */
export const formSubtitle = subtitle => (
  <h4 className="vads-u-font-size--h5 vads-u-color--gray-dark">{subtitle}</h4>
);

/**
 * Creates a formatted title based on parameters provided
 * @param {object} params
 * @param {string} params.title - Base title text.
 * @param {string} params.titleAddition - Text appended after the title.
 * @returns {string} Formatted title text
 */
export const formattedFormTitleText = ({ title, titleAddition }) =>
  `${title}: ${titleAddition}`;

/**
 * Creates a consistent checkbox group UI configuration for conditions
 */
export function makeConditionsUI({
  title,
  description,
  hint,
  replaceSchema,
  updateUiSchema,
}) {
  return checkboxGroupUI({
    title,
    description,
    hint,
    labels: {},
    required: false,
    replaceSchema,
    updateUiSchema,
  });
}

/**
 * Adds an error if the 'none' checkbox is selected along with a new condition
 * @param {object} conditions - Conditions object containing the state of various checkboxes
 * @param {object} errors - Errors object from rjsf
 * @param {string} errorKey - Identifies the section to which the error belongs
 * @param {string} errorMessage - Specific message for 'none' checkbox conflict
 */
export function validateConditions(conditions, errors, errorKey, errorMessage) {
  if (
    conditions?.none === true &&
    Object.values(conditions).filter(value => value === true).length > 1
  ) {
    errors[errorKey].conditions.addError(errorMessage);
  }
}

// Matches the disability name vets-api submits, so keys built from it match
// the toxic exposure lookup downstream.
export const conditionNameWithSide = disability => {
  const condition =
    typeof disability?.condition === 'string'
      ? disability.condition.trim()
      : '';
  if (!condition) return '';

  const side =
    typeof disability?.sideOfBody === 'string'
      ? disability.sideOfBody.trim()
      : '';

  return side ? `${condition}, ${side.toLowerCase()}` : condition;
};

export const sippableIdForCondition = disability =>
  sippableId(conditionNameWithSide(disability) || NULL_CONDITION_STRING);

/**
 * Builds the Schema based on user entered condition names
 * @param {object} formData - Full formData for the form
 * @returns {object} - Object with ids for each condition
 */
export function makeConditionsSchema(formData) {
  // Skip 'blank' and other invalid values (null, empty strings, etc.)
  const ids = (formData?.newDisabilities || [])
    .filter(disability => {
      const { condition } = disability || {};
      return condition && condition.trim() !== '' && condition !== 'blank';
    })
    .map(sippableIdForCondition);

  const sippableOptions = [...new Set(ids)];
  sippableOptions.push('none');

  return checkboxGroupSchema(sippableOptions);
}

/**
 * Formats the parts of a fullName object into a single string, e.g. "Hector Lee Brooks Jr."
 *
 * @param {object} fullName - Object holding name parts
 * @returns {string} Name formatted into a single string. Empty string if all parts are missing.
 */
export const formatFullName = (fullName = {}) => {
  let res = '';
  if (fullName?.first) {
    res += fullName.first;
  }
  if (fullName?.middle) {
    res += ` ${fullName.middle}`;
  }
  if (fullName?.last) {
    res += ` ${fullName.last}`;
  }
  if (fullName?.suffix) {
    res += ` ${fullName.suffix}`;
  }

  return res.trim();
};

/**
 * Checks if
 * 1. The flag for the modern 4142 flow is enabled
 *
 * @param {object} formData
 * @returns {boolean} true if disability526Enable2024Form4142 is present, false otherwise
 */
export function isCompletingModern4142(formData) {
  return formData?.disability526Enable2024Form4142 === true;
}

export const modern4142AuthURL =
  '/supporting-evidence/private-medical-records-authorize-release';

export const legacy4142AuthURL = '/supporting-evidence/private-medical-records';

export const minimum4142Setup = formData => {
  return (
    formData?.['view:hasEvidence'] === true &&
    // And the user is still choosing to include private records
    formData?.['view:selectableEvidenceTypes']?.[
      'view:hasPrivateMedicalRecords'
    ] === true
  );
};

export const baseDoNew4142Logic = formData => {
  return (
    // If flipper is enabled
    formData.disability526Enable2024Form4142 === true &&
    // And the user has evidence for review
    minimum4142Setup(formData) === true &&
    // And the user has previously acknowledged the 4142 authorization
    formData['view:patientAcknowledgement']?.['view:acknowledgement'] ===
      true &&
    // And the user has not switched to another 4142 option (e.g. upload)
    formData?.['view:uploadPrivateRecordsQualifier']?.[
      'view:hasPrivateRecordsToUpload'
    ] !== true &&
    // And the user has not already acknowledged the NEW 4142
    formData?.patient4142Acknowledgement !== true
    // then we must redirect them and show the alert
  );
};

export const redirectWhenFlipperOff = props => {
  const { returnUrl, formData } = props;
  return (
    minimum4142Setup(formData) === true &&
    returnUrl === modern4142AuthURL &&
    formData.disability526Enable2024Form4142 !== true
  );
};

export const redirectWhenNoEvidence = props => {
  const { returnUrl, formData } = props;
  const hasNoEvidence = isEvidenceEnhancement(formData)
    ? !hasMedicalRecords(formData) && !hasOtherEvidence(formData)
    : formData?.['view:hasEvidence'] === false;
  return (
    hasNoEvidence &&
    (returnUrl === modern4142AuthURL || returnUrl === legacy4142AuthURL)
  );
};

/**
 * Redirect paths for file upload pages whose V3/legacy variants live at
 * different paths. Two groups: both are gated by toggle FileInputV3, however
 * the first group is additionally gated by the evidence enhancement toggle.
 */
const UPLOAD_REDIRECT_GROUPS = [
  // group A: pages also gated by the evidence enhancement toggle
  // the mapping is asymmetric because some retired/loop V3 paths
  // resume on the same legacy page (and vice versa)
  {
    secondaryGate: isEvidenceEnhancement,
    v3ToLegacy: {
      '/supporting-evidence/private-medical-records-upload-evidence':
        '/supporting-evidence/private-medical-records-upload-file',
      // Retired single-page V3 path (pre list-and-loop migration)
      '/supporting-evidence/additional-evidence-upload-file':
        '/supporting-evidence/additional-evidence-upload',
      // list-and-loop pages
      '/supporting-evidence/additional-evidence-uploads-intro':
        '/supporting-evidence/additional-evidence-upload',
      '/supporting-evidence/additional-evidence-uploads-summary':
        '/supporting-evidence/additional-evidence-upload',
    },
    legacyToV3: {
      '/supporting-evidence/private-medical-records-upload-file':
        '/supporting-evidence/private-medical-records-upload-evidence',
      '/supporting-evidence/additional-evidence-upload':
        '/supporting-evidence/additional-evidence-uploads-summary',
      // Retired single-page V3 path resumes on the list-and-loop summary
      '/supporting-evidence/additional-evidence-upload-file':
        '/supporting-evidence/additional-evidence-uploads-summary',
    },
  },
  // group B: pages not further gated by the evidence enhancement toggle
  // the mapping is symmetric for this group
  (() => {
    const v3ToLegacy = {
      '/supporting-evidence/service-treatment-records-upload-evidence':
        '/supporting-evidence/service-treatment-records-upload',
    };
    return {
      secondaryGate: () => true,
      v3ToLegacy,
      legacyToV3: Object.fromEntries(
        Object.entries(v3ToLegacy).map(([v3, legacy]) => [legacy, v3]),
      ),
    };
  })(),
];

/**
 * Determines the SIP resume redirect for any V3/legacy upload page pair.
 * Looks up the saved path against the map matching the current FileInputV3
 * toggle direction, for every group whose secondary gate passes. Returns the
 * counterpart path, or null when the saved path already matches the current
 * toggle state (or is unrelated to the upload pages).
 *
 * @param {Object} props - { returnUrl, formData }
 * @returns {string|null} redirect path or null
 */
export const getUploadPageRedirect = ({ returnUrl, formData }) => {
  const direction = formData.disability526SupportingEvidenceFileInputV3
    ? 'legacyToV3'
    : 'v3ToLegacy';
  const group = UPLOAD_REDIRECT_GROUPS.find(
    g => g.secondaryGate(formData) && returnUrl in g[direction],
  );
  return group ? group[direction][returnUrl] : null;
};

export const isNewConditionsOn = formData =>
  !!formData?.disabilityCompNewConditionsWorkflow;

export const isNewConditionsOff = formData => !isNewConditionsOn(formData);

// Regex pattern to match legacy (v1) condition page URLs
const LEGACY_CONDITIONS_URLS =
  /\/(claim-type|disabilities\/(orientation|rated-disabilities|summary)|new-disabilities\/(follow-up|add))(?:\/|$)/;

// For users continuing an IPF that is migrated from the v1 to v2
// conditions flow, if the returnUrl was specific to the v1 flow,
// replace with this v2-specific entry point
export const CONDITIONS_V2_ENTRY = '/conditions/orientation';

// Regex pattern to match /toxic-exposure/* and /pow URLs
// Toxic Exposure and Prisoner of War pages only show when the claim has at
// least one real NEW or SECONDARY condition (hasRealNewOrSecondaryConditions).
// Use this regex in ensuring a TE/POW returnUrl is overridden if the resumed claim
// does not meet this constraint. Override to the v2 conditions summary page.
const TOXIC_EXPOSURE_AND_POW_URLS = /^\/(?:toxic-exposure|pow)(?:\/|$)/;
export const CONDITIONS_V2_SUMMARY = '/conditions/summary';

export const SUPPORTING_EVIDENCE_ENHANCEMENT_ENTRY =
  '/supporting-evidence/evidence-request';

export const SUPPORTING_EVIDENCE_LEGACY_ENTRY =
  '/supporting-evidence/evidence-types';

/**
 * Live feature-toggle values for new-session SIP resume (set during Form526 render).
 * Saved formData can still reflect the prior session; Flipper reflects the new one.
 */
let resumeLiveToggleValues;

export const setResumeLiveToggleValues = liveToggleValues => {
  resumeLiveToggleValues = liveToggleValues;
};

export const mergeFormDataWithLiveResumeToggles = formData => {
  if (!formData || !resumeLiveToggleValues) return formData;
  return { ...formData, ...resumeLiveToggleValues };
};

/**
 * Latest in-progress form data merged with live toggles, set before resume so
 * normalizeReturnUrlForResume and getSafeReturnUrl use the active flow.
 */
let resumeFormDataForReturnUrlNormalization;

export const setResumeFormDataForReturnUrlNormalization = formData => {
  resumeFormDataForReturnUrlNormalization =
    mergeFormDataWithLiveResumeToggles(formData);
};

/**
 * Live conditions workflow transform config (set during Form526 render).
 * Enables onFormLoaded to apply the transformation synchronously before routing,
 * ensuring routing decisions always reflect post-transform data.
 */
let conditionsTransformConfig;

export const setConditionsTransformConfig = config => {
  conditionsTransformConfig = config;
};

/** Clears SIP resume normalization module state (call from tests to avoid cross-test pollution). */
export const resetResumeUrlNormalizationState = () => {
  resumeLiveToggleValues = undefined;
  resumeFormDataForReturnUrlNormalization = undefined;
  conditionsTransformConfig = undefined;
};

const SUPPORTING_EVIDENCE_LEGACY_RESUME_PAGE_CONFIGS = [
  {
    path: '/supporting-evidence/evidence-types',
    depends: fd => !isEvidenceEnhancement(fd),
  },
  {
    path: '/supporting-evidence/private-medical-records-upload',
    depends: fd => !isEvidenceEnhancement(fd),
  },
  {
    path: '/supporting-evidence/additional-evidence',
    depends: fd => !isEvidenceEnhancement(fd),
  },
];

const SUPPORTING_EVIDENCE_ENHANCEMENT_RESUME_PAGE_CONFIGS = [
  {
    path: '/supporting-evidence/evidence-request',
    depends: isEvidenceEnhancement,
  },
  {
    path: '/supporting-evidence/medical-records',
    depends: isEvidenceEnhancement,
  },
  {
    path: '/supporting-evidence/additional-evidence-intro',
    depends: isEvidenceEnhancement,
  },
  {
    path: '/supporting-evidence/private-medical-records-upload-evidence',
    depends: isEvidenceEnhancement,
  },
  {
    path: '/supporting-evidence/private-medical-records-upload-file',
    depends: isEvidenceEnhancement,
  },
  {
    path: '/supporting-evidence/additional-evidence-upload-file',
    depends: isEvidenceEnhancement,
  },
  {
    path: '/supporting-evidence/additional-evidence-uploads-intro',
    depends: isEvidenceEnhancement,
  },
  {
    path: '/supporting-evidence/additional-evidence-uploads-summary',
    depends: isEvidenceEnhancement,
  },
  {
    path: '/supporting-evidence/additional-evidence-upload',
    depends: isEvidenceEnhancement,
  },
];

/**
 * Rewrites supporting-evidence SIP returnUrls that fail flow toggle depends on
 * new-session resume so checkValidPagePath does not fall back to form beginning.
 *
 * @param {string} returnUrl - URL from SIP metadata
 * @param {Object} [formData] - form data with live toggle values merged in
 * @returns {string}
 */
export const normalizeSupportingEvidenceReturnUrl = (returnUrl, formData) => {
  if (!returnUrl || typeof returnUrl !== 'string') return returnUrl;
  if (!formData || typeof formData !== 'object') return returnUrl;

  if (
    SUPPORTING_EVIDENCE_LEGACY_RESUME_PAGE_CONFIGS.some(
      ({ path, depends }) => path === returnUrl && !depends(formData),
    )
  ) {
    return SUPPORTING_EVIDENCE_ENHANCEMENT_ENTRY;
  }

  if (
    SUPPORTING_EVIDENCE_ENHANCEMENT_RESUME_PAGE_CONFIGS.some(
      ({ path, depends }) => path === returnUrl && !depends(formData),
    )
  ) {
    return SUPPORTING_EVIDENCE_LEGACY_ENTRY;
  }

  return returnUrl;
};

/**
 * Rewrites conditions SIP returnUrls that are no longer valid:
 * - a returnUrl saved on a deprecated v1 conditions page
 *    --> route to the conditions orientation page
 * - a Toxic Exposure / PoW returnUrl when the resumed claim has no NEW/SECONDARY condition
 *    --> route to the conditions summary page
 *
 * Sibling of normalizeSupportingEvidenceReturnUrl.
 *
 * The conditions v1 flow is assumed fully inactive, thus only v2-specific conditions
 * urls are assigned.
 *
 * @param {string} returnUrl - URL from SIP metadata
 * @param {Object} [formData] - form data with live toggle values merged in
 * @returns {string} reconciled returnUrl (unchanged when no reconciliation applies)
 */
export const normalizeConditionsReturnUrl = (returnUrl, formData) => {
  if (!returnUrl || typeof returnUrl !== 'string') return returnUrl;
  if (!formData || typeof formData !== 'object') return returnUrl;

  // reconcile a returnUrl saved on the deprecated v1 conditions flow.
  if (isNewConditionsOn(formData) && LEGACY_CONDITIONS_URLS.test(returnUrl)) {
    return CONDITIONS_V2_ENTRY;
  }

  // reconcile a Toxic Exposure / PoW returnUrl when the resumed claim has no
  // real NEW/SECONDARY condition
  if (
    TOXIC_EXPOSURE_AND_POW_URLS.test(returnUrl) &&
    !hasRealNewOrSecondaryConditions(formData)
  ) {
    return CONDITIONS_V2_SUMMARY;
  }

  return returnUrl;
};

/**
 * Redirect-time normalization: rewrite array-builder item page returnUrls to
 * their summary page so resume lands on a stable page (avoids redirect loops
 * when item pages require ?add=true or ?edit=true).
 * Use as formConfig.normalizeReturnUrl for redirect-time normalization.
 *
 * @param {string} returnUrl - URL from SIP metadata
 * @returns {string} returnUrl to use for redirect (summary path if item page, else unchanged)
 */
export const normalizeReturnUrlForResume = returnUrl => {
  if (!returnUrl || typeof returnUrl !== 'string') return returnUrl;

  const formData = resumeFormDataForReturnUrlNormalization;

  let normalizedUrl = normalizeSupportingEvidenceReturnUrl(returnUrl, formData);
  normalizedUrl = normalizeConditionsReturnUrl(normalizedUrl, formData);

  const conditionsItemPage = new RegExp(
    `(.*)(conditions)/\\d+/(?:condition|new-condition-date|new-condition|rated-disability-date|side-of-body|cause-new|cause-secondary|cause-worsened|cause-va|cause)`,
  );
  const traumaticEventsItemPage = new RegExp(
    `(.*)(mental-health-form-0781)/\\d+/(?:event-details|event-report|event-police-report)`,
  );

  if (conditionsItemPage.test(normalizedUrl)) {
    normalizedUrl = normalizedUrl.replace(conditionsItemPage, '$1$2/summary');
  }
  if (traumaticEventsItemPage.test(normalizedUrl)) {
    normalizedUrl = normalizedUrl.replace(
      traumaticEventsItemPage,
      '$1$2/events-summary',
    );
  }
  return normalizedUrl;
};

const isRedirectingFromInvalidBddShaWorkflow = ({
  returnUrl: returnPath,
  formData,
  router,
}) => {
  /**
   * This is a simulation of a future system that would generically handle
   * `returnUrl` invalidity, where invalidity is defined as referring to a page
   * with a `depends` test that fails. If it would not result in a circular
   * import, we'd reuse the form page configs as the source of truth rather than
   * duplicate a subset of them here.
   */
  const pageConfigs = [
    {
      path: '/supporting-evidence/separation-health-assessment',
      depends: isBddShaWorkflowActive,
    },
    {
      path: '/supporting-evidence/separation-health-assessment-upload',
      depends: fd =>
        fd.disability526SupportingEvidenceFileInputV3 && isUploadingBddSha(fd),
    },
    {
      path: '/supporting-evidence/separation-health-assessment-upload-v1',
      depends: fd =>
        !fd.disability526SupportingEvidenceFileInputV3 && isUploadingBddSha(fd),
    },
  ];

  const isInvalid = pageConfigs.some(
    ({ path, depends }) => path === returnPath && !depends(formData),
  );

  const redirectPath = '/supporting-evidence/orientation';
  if (isInvalid) router.push(redirectPath);
  return isInvalid;
};

/**
 * Legacy pages become invalid when the toggle is ON (redirect → evidence-request).
 * Enhancement pages become invalid when the toggle is OFF (redirect → evidence-types).
 */
const isRedirectingFromInvalidEvidenceFlow = ({
  returnUrl: returnPath,
  formData,
  router,
}) => {
  const normalizedPath = normalizeSupportingEvidenceReturnUrl(
    returnPath,
    formData,
  );

  if (normalizedPath !== returnPath) {
    router.push(normalizedPath);
    return true;
  }

  return false;
};

/**
 * Returns true if the formData uses the multistep conditions (v2 flow) data model
 * and the returnUrl points to a legacy conditions (v1 flow) url
 *
 * @param {Object} props - { returnUrl, formData }
 * @returns {boolean}
 */
export const redirectToNewConditions = ({ returnUrl, formData }) => {
  if (!returnUrl || !formData) return false;
  return isNewConditionsOn(formData) && LEGACY_CONDITIONS_URLS.test(returnUrl);
};

/**
 * Transforms SIPs to the target conditions workflow data model (v1 or v2)
 *
 * @param {object} formData - formData
 * @returns {object} post-transform formData (unchanged if no transformation needed)
 */
const applyConditionsDataTransform = formData => {
  if (!conditionsTransformConfig) return formData;

  const { formData: formDataPostTransform, wasTransformed: wasDataModified } =
    applyConditionsWorkflowTransformation(formData, conditionsTransformConfig);
  if (wasDataModified) {
    // Session-scoped counter surfaced as RUM global context by Form526EZApp
    // (526conditionsTransformCount) to detect repeated transform loops.
    incrementConditionsTransformCount();
    conditionsTransformConfig.setFormData(formDataPostTransform);
  }
  return formDataPostTransform;
};

/**
 * Derives missing evidence view fields for resumed IPFs and persists the result
 * before routing/validation, so veterans who already completed the legacy
 * supporting-evidence flow aren't blocked at submission by the enhanced flow's
 * required `view:hasMedicalRecords` field.
 *
 * @param {object} formData
 * @returns {object} reconciled formData (unchanged unless a value was derived)
 */
const applyEvidenceReconciliation = formData => {
  const reconciled = reconcileEvidenceViewFields(formData);
  if (reconciled !== formData) {
    conditionsTransformConfig?.setFormData?.(reconciled);
  }
  return reconciled;
};

// Migrations 11 and 12 make the same correction but run at most once per form,
// so data that goes stale afterwards is only caught here. Writing on every load
// would re-enter this path on a remount.
const applyDisabilityReconciliation = (formData, reconcile) => {
  if (typeof reconcile !== 'function') return formData;

  const reconciled = reconcile(formData);
  if (reconciled !== formData) {
    conditionsTransformConfig?.setFormData?.(reconciled);
  }
  return reconciled;
};

export const onFormLoaded = props => {
  const {
    returnUrl,
    router,
    currentLocation,
    isStartingOver,
    newScreenerQuestionFlow,
    reconcileDisabilityReferences,
  } = props;

  // Ahead of the transforms below: a restart throws this form data away, and
  // the push re-enters here synchronously before the load status resets.
  const restartDestination = prepareRestartRedirect({
    isStartingOver,
    destination: getRestartDestination({ newScreenerQuestionFlow }),
  });
  if (restartDestination) {
    if (!currentLocation?.pathname?.endsWith(restartDestination)) {
      router.push(restartDestination);
    }
    return;
  }

  let formData = mergeFormDataWithLiveResumeToggles(props.formData);
  formData = applyConditionsDataTransform(formData);
  formData = applyEvidenceReconciliation(formData);
  formData = applyDisabilityReconciliation(
    formData,
    reconcileDisabilityReferences,
  );

  // Verify a prefilled mailing-address zip that the user may never edit. The
  // ZipCodeInput component only fires the validation API on keystroke, so an
  // invalid prefilled zip would otherwise never be verified and would bypass
  // validation entirely. Fire-and-forget: routing continues synchronously
  // below; the result is read only by the submit-time check
  // (getCustomValidationErrors), so it does NOT block per-page Continue.
  verifyPrefilledZipCode(formData?.mailingAddress?.zipCode);

  const evidenceFlowProps = { returnUrl, formData, router };
  const conditionsProps = { returnUrl, formData };
  const shouldRedirectToModern4142Choice = baseDoNew4142Logic(formData);
  const shouldRevertWhenFlipperOff = redirectWhenFlipperOff(evidenceFlowProps);
  const shouldRevertWhenNoEvidence = redirectWhenNoEvidence(evidenceFlowProps);
  const uploadPageRedirect = getUploadPageRedirect(evidenceFlowProps);
  const redirectUrl = legacy4142AuthURL;
  const shouldRedirectLegacyConditionsToV2 =
    redirectToNewConditions(conditionsProps);

  if (isRedirectingFromInvalidBddShaWorkflow(evidenceFlowProps)) return;
  if (isRedirectingFromInvalidEvidenceFlow(evidenceFlowProps)) return;

  if (shouldRedirectToModern4142Choice === true) {
    // if we should redirect to the modern 4142 choice page, we set the shared variable
    // and redirect to the redirectUrl (the modern 4142 choice page)
    setSharedVariable('alertNeedsShown4142', shouldRedirectToModern4142Choice);
    router.push(redirectUrl);
  } else if (
    // if the returnUrl is the modern 4142 choice page and the flipper is not enabled,
    // then we toggled flipper on, the user got to this page, then we turned the flipper off
    // this happens a lot in development and testing but would only happen if we do a rollback in production
    // if the user is set to redirect to a page that is set to be hidden they get stuck in a loop so we must place them on the previous page
    shouldRedirectToModern4142Choice === false &&
    shouldRevertWhenFlipperOff === true
  ) {
    router.push(redirectUrl);
  } else if (
    shouldRedirectToModern4142Choice === false &&
    shouldRevertWhenNoEvidence === true
  ) {
    router.push(
      isEvidenceEnhancement(formData)
        ? SUPPORTING_EVIDENCE_ENHANCEMENT_ENTRY
        : SUPPORTING_EVIDENCE_LEGACY_ENTRY,
    );
  } else if (uploadPageRedirect) {
    // pages for uploading files have different paths based on migrations to v3 file upload component
    // and a list-and-loop implementation for additional evidence
    router.push(uploadPageRedirect);
  } else if (shouldRedirectLegacyConditionsToV2) {
    // legacy conditions page saved but form data reflects the v2 flow → swap to conditions orientation
    router.push(CONDITIONS_V2_ENTRY);
  } else {
    router.push(returnUrl);
  }
};
